// frontend/src/components/profile/BulkRequestManager.js
import React, { useState, useEffect, useRef } from 'react';
import { bulkApi } from '../../api/bulk';
import helpers from '../../utils/helpers';
import { FaPlus, FaTrash, FaBoxOpen, FaCheckCircle, FaTimesCircle, FaArrowLeft, FaSpinner, FaInfoCircle, FaPhoneAlt, FaEnvelope, FaLock, FaChevronRight, FaBell, FaChevronDown, FaCheck } from 'react-icons/fa';

// --- UNIT OPTIONS ---
const UNIT_OPTIONS = [
    "Pieces (Pcs)",
    "Units",
    "Pairs",
    "Sets",
    "Kits",
    "Boxes",
    "Packs / Packets",
    "Rolls",
    "Cartons",
    "Cases",
    "Bundles",
    "Drums / Barrels",
    "Jerry Cans / Kegs",
    "Pallets",
    "Crates",
    "Containers (20ft)",
    "Containers (40ft)"
];

// --- UNIT PICKER ---
// Replaces the phone's own dropdown. A long native list pops up over the
// top of the screen on some phones; this one opens right under the box,
// shows about six units at a time and scrolls inside itself.
function UnitPicker({ value, onChange, options }) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const wrapRef = useRef(null);
  const listRef = useRef(null);
  const buttonRef = useRef(null);

  // Close when tapping anywhere else.
  useEffect(() => {
    if (!open) return;
    const close = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    document.addEventListener('touchstart', close);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('touchstart', close);
    };
  }, [open]);

  // Scrolls only INSIDE the list (never the page) to show one option.
  const showOption = (idx, centre = false) => {
    const list = listRef.current;
    const el = list && list.children[idx];
    if (!el) return;
    if (centre) {
      list.scrollTop = el.offsetTop - list.clientHeight / 2 + el.clientHeight / 2;
    } else if (el.offsetTop < list.scrollTop) {
      list.scrollTop = el.offsetTop;
    } else if (el.offsetTop + el.offsetHeight > list.scrollTop + list.clientHeight) {
      list.scrollTop = el.offsetTop + el.offsetHeight - list.clientHeight;
    }
  };

  // On opening: highlight the current unit, then move the PAGE down just
  // enough that the whole list is visible above the phone's bottom menu bar.
  useEffect(() => {
    if (!open || !listRef.current) return;
    const idx = Math.max(0, options.indexOf(value));
    setActive(idx);
    showOption(idx, true);
    const list = listRef.current;
    list.focus({ preventScroll: true });
    const bottomBar = window.innerWidth < 768 ? 72 : 8; // MobileBottomNav is h-16
    const hiddenBy = list.getBoundingClientRect().bottom - (window.innerHeight - bottomBar);
    if (hiddenBy > 0) window.scrollBy({ top: hiddenBy, behavior: 'smooth' });
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Keep the keyboard-highlighted option in view.
  useEffect(() => {
    if (open) showOption(active);
  }, [active, open]); // eslint-disable-line react-hooks/exhaustive-deps

  const choose = (opt) => {
    onChange(opt);
    setOpen(false);
    if (buttonRef.current) buttonRef.current.focus({ preventScroll: true });
  };

  const onListKey = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive(a => Math.min(options.length - 1, a + 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive(a => Math.max(0, a - 1)); }
    else if (e.key === 'Home') { e.preventDefault(); setActive(0); }
    else if (e.key === 'End') { e.preventDefault(); setActive(options.length - 1); }
    else if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); choose(options[active]); }
    else if (e.key === 'Escape') { e.preventDefault(); setOpen(false); buttonRef.current && buttonRef.current.focus(); }
    else if (e.key === 'Tab') { setOpen(false); }
  };

  return (
    <div ref={wrapRef} className="relative w-full">
      <button
        ref={buttonRef}
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`Unit: ${value}`}
        onClick={() => setOpen(o => !o)}
        onKeyDown={(e) => { if (e.key === 'ArrowDown' || e.key === 'ArrowUp') { e.preventDefault(); setOpen(true); } }}
        className={`w-full p-2 border rounded bg-white text-sm text-left flex items-center justify-between gap-2 ${open ? 'ring-2 ring-primary' : ''}`}
      >
        <span className="truncate">{value}</span>
        <FaChevronDown className={`flex-shrink-0 text-xs transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <ul
          ref={listRef}
          role="listbox"
          tabIndex={-1}
          aria-label="Choose a unit"
          aria-activedescendant={`unit-opt-${active}`}
          onKeyDown={onListKey}
          className="absolute left-0 right-0 md:right-auto md:w-56 top-full mt-1 z-30 max-h-56 overflow-y-auto overscroll-contain bg-white border rounded shadow-lg py-1 focus:outline-none"
        >
          {options.map((opt, i) => {
            const selected = opt === value;
            return (
              <li
                key={opt}
                id={`unit-opt-${i}`}
                role="option"
                aria-selected={selected}
                onMouseMove={() => { if (active !== i) setActive(i); }}  // real mouse movement only; scrolling the list must not steal the highlight
                onClick={() => choose(opt)}
                className={`px-3 py-2.5 text-sm cursor-pointer flex items-center justify-between gap-2 ${i === active ? 'bg-blue-50' : ''} ${selected ? 'font-bold text-primary' : ''}`}
              >
                <span>{opt}</span>
                {selected && <FaCheck className="text-xs flex-shrink-0" />}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

// One format for every Nigerian number: +234XXXXXXXXXX.
const normalizeNgPhone = (raw) => {
  let digits = String(raw || '').replace(/\D/g, '');
  if (digits.startsWith('2340')) digits = '234' + digits.slice(4);
  else if (digits.startsWith('0')) digits = '234' + digits.slice(1);
  else if (digits.length === 10) digits = '234' + digits;
  return digits ? `+${digits}` : '';
};

const looksLikeEmail = (raw) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(String(raw || '').trim())
  && !String(raw).trim().toLowerCase().endsWith('@phone.user');

function BulkRequestManager({ onUnreadChange }) {
  const [view, setView] = useState('list'); // list, create, detail
  const [requests, setRequests] = useState([]);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  // Create Form State
  const [newItems, setNewItems] = useState([
    { part_name: '', quantity: 1, unit: 'Pieces (Pcs)', target_price: '', notes: '' }
  ]);
  const [requestNotes, setRequestNotes] = useState('');

  // Contact details: every bulk request needs BOTH a phone and an email.
  const [contact, setContact] = useState({ phone: '', email: '', phone_locked: false, email_locked: false });
  const [contactLoading, setContactLoading] = useState(false);
  const [contactError, setContactError] = useState('');

  const openCreate = async () => {
    setView('create');
    setContactError('');
    setContactLoading(true);
    try {
      const data = await bulkApi.getContact();
      setContact({
        phone: data.phone || '',
        email: data.email || '',
        phone_locked: !!data.phone_locked,
        email_locked: !!data.email_locked,
      });
    } catch (err) {
      console.error("Failed to load contact details", err);
    } finally {
      setContactLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const data = await bulkApi.getMyRequests();
      if (onUnreadChange) {
        onUnreadChange((data || []).filter(r => r.customer_unread).length);
      }
      setRequests(data);
    } catch (err) {
      console.error("Failed to load requests", err);
    } finally {
      setLoading(false);
    }
  };

  // --- CREATE FORM HANDLERS ---
  const handleItemChange = (index, field, value) => {
    const updated = [...newItems];
    updated[index][field] = value;
    setNewItems(updated);
  };

  const addItemRow = () => {
    setNewItems([...newItems, { part_name: '', quantity: 1, unit: 'Pieces (Pcs)', target_price: '', notes: '' }]);
  };

  const removeItemRow = (index) => {
    if (newItems.length === 1) return;
    setNewItems(newItems.filter((_, i) => i !== index));
  };

  const handleSubmitRequest = async (e) => {
    e.preventDefault();
    
    // Validation
    if (newItems.some(i => !i.part_name.trim())) {
        alert("Please enter a Part Name for all items.");
        return;
    }

    // Both a phone number and an email are required for every bulk request.
    const phone = normalizeNgPhone(contact.phone);
    if (!contact.phone.trim()) {
        setContactError("Please add your phone number. We need it to reach you about this quote.");
        return;
    }
    if (phone.length !== 14) {
        setContactError("Please enter a valid Nigerian phone number, e.g. 0803 123 4567.");
        return;
    }
    // Email is optional - but if something is typed it must look right.
    if (contact.email.trim() && !looksLikeEmail(contact.email)) {
        setContactError("Please enter a valid email address, e.g. name@example.com, or leave the email box empty.");
        return;
    }
    setContactError('');

    setSubmitting(true);
    try {
      const payload = {
        items: newItems.map(i => ({
            ...i,
            quantity: parseInt(i.quantity),
            target_price: i.target_price ? parseFloat(i.target_price) : null
        })),
        notes: requestNotes,
        // Only the one the customer typed is sent; the verified one comes
        // from their account on the server.
        contact_phone: contact.phone_locked ? null : phone,
        contact_email: contact.email_locked ? null : contact.email.trim()
      };

      await bulkApi.createRequest(payload);
      alert("Bulk request submitted successfully!");
      setView('list');
      setNewItems([{ part_name: '', quantity: 1, unit: 'Pieces (Pcs)', target_price: '', notes: '' }]);
      setRequestNotes('');
      fetchRequests();
    } catch (err) {
      const msg = err?.message || "Failed to submit request.";
      // Contact problems are shown next to the contact boxes.
      if (/phone|email/i.test(msg)) setContactError(msg);
      else alert(msg);
    } finally {
      setSubmitting(false);
    }
  };

  // --- DETAIL VIEW HANDLERS ---
  const handleViewDetail = (req) => {
    if (req.customer_unread) {
      // Update the screen at once, then tell the server.
      const next = requests.map(r => r.id === req.id ? { ...r, customer_unread: false } : r);
      setRequests(next);
      if (onUnreadChange) onUnreadChange(next.filter(r => r.customer_unread).length);
      bulkApi.markRead(req.id).catch(err => console.error("Could not mark request as read", err));
    }
      setSelectedRequest(req);
      setView('detail');
  };

  const handleStatusUpdate = async (status) => {
      const accepting = status === 'accepted';
      const question = accepting
          ? `Accept this quote for ${helpers.formatCurrency(selectedRequest.total_quote_amount, 'NGN')}?`
          : "Decline this quote?";
      if (!window.confirm(question)) return;
      setSubmitting(true);
      try {
          await bulkApi.updateStatus(selectedRequest.id, status);
          alert(accepting
              ? "Quote accepted. Our team will contact you with the invoice and delivery details."
              : "Quote declined.");
          setView('list');
          fetchRequests();
      } catch (err) {
          alert(err?.message || "Something went wrong. Please try again.");
      } finally {
          setSubmitting(false);
      }
  };

  const itemCount = (req) => {
      const n = (req.items || []).length;
      return `${n} item${n === 1 ? '' : 's'} requested`;
  };

  // What the bottom of each card says, so customers know to tap it.
  const renderCardAction = (req) => {
      if (req.status === 'quoted') {
          return (
              <div className="mt-3 w-full flex items-center justify-between gap-2 bg-green-600 text-white rounded-md px-3 py-2.5 text-sm font-bold">
                  <span>Quote ready: review &amp; accept</span>
                  <FaChevronRight className="flex-shrink-0" />
              </div>
          );
      }
      const text = {
          pending: "We are preparing your quote. View request",
          accepted: "Quote accepted. View details",
          rejected: "Quote declined. View details",
      }[req.status] || "View details";
      return (
          <div className="mt-3 pt-3 border-t border-slate-200 flex items-center justify-between gap-2 text-xs font-semibold text-primary">
              <span>{text}</span>
              <FaChevronRight className="flex-shrink-0" />
          </div>
      );
  };

  const getStatusBadge = (status) => {
      const styles = {
          pending: "bg-yellow-100 text-yellow-800",
          quoted: "bg-blue-100 text-blue-800",
          accepted: "bg-green-100 text-green-800",
          rejected: "bg-red-100 text-red-800"
      };
      return <span className={`px-2 py-1 rounded text-xs font-bold uppercase ${styles[status] || 'bg-gray-100'}`}>{status}</span>;
  };

  if (loading) return <div className="text-center py-10">Loading requests...</div>;

  // --- VIEW 1: LIST ---
  if (view === 'list') {
    return (
      <div>
        {/* UPDATED HEADER: Stack on mobile, Row on desktop */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
          <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
            <FaBoxOpen className="text-primary" /> Bulk Purchases
          </h2>
          <button 
            onClick={openCreate}
            className="bg-primary hover:bg-primary-dark text-white text-sm font-bold py-2 px-4 rounded flex items-center justify-center gap-2 transition duration-300 w-full sm:w-auto"
          >
            <FaPlus /> New Request
          </button>
        </div>

        {/* --- TOOLKIT --- */}
        <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-6 rounded-r-lg shadow-sm flex items-start gap-3">
            <FaInfoCircle className="text-blue-500 text-xl mt-1 flex-shrink-0" />
            <div>
                <h4 className="font-bold text-blue-800 text-sm">Get Custom Quotes</h4>
                <p className="text-sm text-blue-700">
                    Need parts in large quantities? Submit a list of items and your target price. Our team will review and send you a competitive quote.
                </p>
            </div>
        </div>

        {requests.length === 0 ? (
          <div className="text-center py-10 bg-gray-50 rounded-lg border border-dashed border-gray-300">
            <p className="text-gray-500 mb-4">No bulk requests found.</p>
            <button onClick={openCreate} className="text-primary hover:underline">Create your first request</button>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Tell the customer plainly that a quote is waiting for them */}
            {requests.some(r => r.status === 'quoted') && (
              <div className="bg-green-50 border border-green-200 text-green-800 rounded-lg p-3 flex items-start gap-3 text-sm">
                <FaBell className="mt-0.5 flex-shrink-0" />
                <p>
                  {(() => {
                    const n = requests.filter(r => r.status === 'quoted').length;
                    return n === 1
                      ? 'You have a quote waiting for your answer. Open it below to review the prices and accept or decline.'
                      : `You have ${n} quotes waiting for your answer. Open each one below to review the prices and accept or decline.`;
                  })()}
                </p>
              </div>
            )}

            {requests.map(req => (
              <div
                key={req.id}
                role="button"
                tabIndex={0}
                onClick={() => handleViewDetail(req)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleViewDetail(req); } }}
                className={`bg-white border rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary ${req.customer_unread ? 'border-red-300 ring-1 ring-red-200' : 'border-gray-200'}`}
              >
                {/* Row 1: ID + badges (wraps on narrow screens) */}
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-mono font-bold text-gray-700 whitespace-nowrap">{req.request_id}</span>
                  {getStatusBadge(req.status)}
                  {req.customer_unread && (
                    <span className="text-[10px] font-bold uppercase bg-red-500 text-white px-2 py-0.5 rounded-full">New</span>
                  )}
                </div>

                {/* Row 2: details + amount (amount drops below on narrow screens) */}
                <div className="mt-2 flex flex-wrap items-end justify-between gap-x-4 gap-y-1">
                  <div className="min-w-0">
                    <p className="text-sm text-gray-600">{itemCount(req)}</p>
                    <p className="text-xs text-gray-400">{new Date(req.created_at).toLocaleDateString()}</p>
                  </div>
                  {req.status === 'quoted' || req.status === 'accepted' ? (
                    <div className="text-lg font-bold text-primary whitespace-nowrap">
                      {helpers.formatCurrency(req.total_quote_amount, 'NGN')}
                    </div>
                  ) : (
                    <span className="text-xs text-gray-400 italic whitespace-nowrap">
                      {req.status === 'rejected' ? 'Declined' : 'Pending quote'}
                    </span>
                  )}
                </div>

                {renderCardAction(req)}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // --- VIEW 2: CREATE ---
  if (view === 'create') {
      return (
          <div>
              <button onClick={() => setView('list')} className="text-gray-500 hover:text-primary flex items-center gap-2 mb-4">
                  <FaArrowLeft /> Back to List
              </button>
              <h2 className="text-xl font-bold text-gray-800 mb-4">Create Bulk Request</h2>
              
              <div className="bg-blue-50 p-4 rounded mb-6 text-sm text-blue-800">
                  <p>List the parts you need below. Be specific about the <strong>Unit</strong> (e.g., Cartons vs Pieces) to ensure an accurate quote.</p>
              </div>

              <form onSubmit={handleSubmitRequest}>
                  <div className="space-y-4 mb-6">
                      {newItems.map((item, index) => (
                          <div key={index} className="flex flex-col md:flex-row gap-2 items-start md:items-center bg-gray-50 p-3 rounded border border-gray-200">
                              <div className="flex-grow w-full">
                                  <input 
                                      placeholder="Part Name / Number *" 
                                      value={item.part_name}
                                      onChange={(e) => handleItemChange(index, 'part_name', e.target.value)}
                                      className="w-full p-2 border rounded"
                                      required
                                  />
                              </div>
                              
                              {/* QUANTITY */}
                              <div className="w-full md:w-20">
                                  <input 
                                      type="number" 
                                      placeholder="Qty" 
                                      value={item.quantity}
                                      onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                                      className="w-full p-2 border rounded text-center"
                                      min="1"
                                      required
                                  />
                              </div>

                              {/* UNIT DROPDOWN */}
                              <div className="w-full md:w-40">
                                  <UnitPicker
                                      value={item.unit}
                                      onChange={(v) => handleItemChange(index, 'unit', v)}
                                      options={UNIT_OPTIONS}
                                  />
                              </div>

                              <div className="w-full md:w-32">
                                  <input 
                                      type="number" 
                                      placeholder="Target Price" 
                                      value={item.target_price}
                                      onChange={(e) => handleItemChange(index, 'target_price', e.target.value)}
                                      className="w-full p-2 border rounded"
                                  />
                              </div>
                              <div className="w-full md:w-48">
                                  <input 
                                      placeholder="Notes (Optional)" 
                                      value={item.notes}
                                      onChange={(e) => handleItemChange(index, 'notes', e.target.value)}
                                      className="w-full p-2 border rounded"
                                  />
                              </div>
                              <button 
                                  type="button" 
                                  onClick={() => removeItemRow(index)}
                                  className="text-red-500 hover:text-red-700 p-2"
                                  disabled={newItems.length === 1}
                              >
                                  <FaTrash />
                              </button>
                          </div>
                      ))}
                  </div>

                  <button type="button" onClick={addItemRow} className="text-primary font-bold text-sm flex items-center gap-2 mb-6 hover:underline">
                      <FaPlus /> Add Another Item
                  </button>

                  {/* --- CONTACT DETAILS (both required) --- */}
                  <div className={`mb-6 p-4 rounded border ${contactError ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-gray-50'}`}>
                      <h3 className="text-sm font-bold text-gray-800 mb-1">How we will reach you about this quote</h3>
                      <p className="text-xs text-gray-600 mb-3">
                          Your phone number is required. Adding an email is optional, but recommended:
                          we will then send your quote by email as well as by SMS.
                      </p>

                      {contactLoading ? (
                          <div className="text-sm text-gray-500 flex items-center gap-2"><FaSpinner className="animate-spin" /> Loading your details...</div>
                      ) : (
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                              <div>
                                  <label htmlFor="bulkPhone" className="block text-xs font-bold text-gray-700 mb-1">
                                      <FaPhoneAlt className="inline mr-1" /> Phone Number <span className="text-red-500">*</span>
                                  </label>
                                  <div className="relative">
                                      <input
                                          id="bulkPhone"
                                          type="tel"
                                          autoComplete="tel"
                                          value={contact.phone}
                                          onChange={(e) => { setContact(prev => ({ ...prev, phone: e.target.value })); setContactError(''); }}
                                          readOnly={contact.phone_locked}
                                          placeholder="e.g. 0803 123 4567"
                                          className={`w-full p-2 border rounded ${contact.phone_locked ? 'bg-gray-100 text-gray-600 pr-8' : 'bg-white'}`}
                                      />
                                      {contact.phone_locked && <FaLock className="absolute right-2 top-3 text-gray-400 text-xs" title="Verified at sign-up" />}
                                  </div>
                                  <p className="text-[11px] text-gray-500 mt-1">
                                      {contact.phone_locked ? 'Verified when you signed up.' : 'Saved to your profile for next time.'}
                                  </p>
                              </div>
                              <div>
                                  <label htmlFor="bulkEmail" className="block text-xs font-bold text-gray-700 mb-1">
                                      <FaEnvelope className="inline mr-1" /> Email Address <span className="text-gray-400 font-normal">(optional)</span>
                                  </label>
                                  <div className="relative">
                                      <input
                                          id="bulkEmail"
                                          type="email"
                                          autoComplete="email"
                                          value={contact.email}
                                          onChange={(e) => { setContact(prev => ({ ...prev, email: e.target.value })); setContactError(''); }}
                                          readOnly={contact.email_locked}
                                          placeholder="e.g. name@example.com"
                                          className={`w-full p-2 border rounded ${contact.email_locked ? 'bg-gray-100 text-gray-600 pr-8' : 'bg-white'}`}
                                      />
                                      {contact.email_locked && <FaLock className="absolute right-2 top-3 text-gray-400 text-xs" title="Verified at sign-up" />}
                                  </div>
                                  <p className="text-[11px] text-gray-500 mt-1">
                                      {contact.email_locked ? 'Verified when you signed up.' : 'Recommended. Saved to your profile for next time.'}
                                  </p>
                              </div>
                          </div>
                      )}

                      {contactError && (
                          <p className="text-sm text-red-600 font-semibold mt-3">{contactError}</p>
                      )}
                  </div>

                  <div className="mb-6">
                      <label className="block text-sm font-bold text-gray-700 mb-1">Additional Notes</label>
                      <textarea 
                          rows="3" 
                          value={requestNotes}
                          onChange={(e) => setRequestNotes(e.target.value)}
                          className="w-full p-2 border rounded"
                          placeholder="Any specific instructions for this order..."
                      ></textarea>
                  </div>

                  <button 
                      type="submit" 
                      disabled={submitting || contactLoading}
                      className="w-full bg-primary hover:bg-primary-dark text-white font-bold py-3 rounded transition duration-300 flex justify-center items-center gap-2"
                  >
                      {submitting ? <FaSpinner className="animate-spin" /> : <FaCheckCircle />} Submit Request
                  </button>
              </form>
          </div>
      );
  }

  // --- VIEW 3: DETAIL ---
  if (view === 'detail' && selectedRequest) {
      const req = selectedRequest;
      const hasQuote = req.total_quote_amount > 0;
      const money = (v) => (v || v === 0) && v !== '' ? helpers.formatCurrency(v, 'NGN') : '-';

      return (
          <div>
              <button onClick={() => setView('list')} className="text-gray-500 hover:text-primary flex items-center gap-2 mb-4">
                  <FaArrowLeft /> Back to List
              </button>

              {/* Header: wraps on narrow screens instead of breaking the ID */}
              <div className="mb-4">
                  <div className="flex flex-wrap items-center gap-2">
                      <h2 className="text-xl font-bold text-gray-800">
                          Request <span className="whitespace-nowrap">{req.request_id}</span>
                      </h2>
                      {getStatusBadge(req.status)}
                  </div>
                  <p className="text-sm text-gray-500 mt-1">Created: {new Date(req.created_at).toLocaleString()}</p>
              </div>

              {/* --- QUOTE READY: total + clear instructions + buttons --- */}
              {req.status === 'quoted' && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                      <p className="text-sm font-bold text-green-800">Your quote is ready</p>
                      <p className="text-xl sm:text-2xl font-bold text-green-900 my-1 whitespace-nowrap">{money(req.total_quote_amount)}</p>
                      {req.valid_until && (
                          <p className="text-xs text-green-700 mb-2">Valid until {new Date(req.valid_until).toLocaleDateString()}</p>
                      )}
                      <p className="text-sm text-green-800 mb-3">
                          Check the prices below, then choose <strong>Accept Quote</strong> to go ahead, or <strong>Decline</strong> if it does not suit you.
                      </p>
                      <div className="flex flex-col sm:flex-row gap-2">
                          <button
                              onClick={() => handleStatusUpdate('accepted')}
                              disabled={submitting}
                              className="w-full sm:w-auto bg-green-600 text-white px-5 py-3 rounded font-bold hover:bg-green-700 flex items-center justify-center gap-2 disabled:opacity-60"
                          >
                              {submitting ? <FaSpinner className="animate-spin" /> : <FaCheckCircle />} Accept Quote
                          </button>
                          <button
                              onClick={() => handleStatusUpdate('rejected')}
                              disabled={submitting}
                              className="w-full sm:w-auto bg-white border border-red-200 text-red-600 px-5 py-3 rounded font-bold hover:bg-red-50 flex items-center justify-center gap-2 disabled:opacity-60"
                          >
                              <FaTimesCircle /> Decline
                          </button>
                      </div>
                  </div>
              )}

              {req.status === 'pending' && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6 text-sm text-yellow-800">
                      Our team is preparing your quote. We will send you an SMS{req.user_email && !String(req.user_email).endsWith('@phone.user') ? ' and an email' : ''} when it is ready.
                  </div>
              )}

              {/* --- ITEMS: cards on phones --- */}
              <div className="sm:hidden space-y-3 mb-6">
                  {req.items.map((item, idx) => (
                      <div key={idx} className="border border-gray-200 rounded-lg p-3">
                          <p className="font-bold text-gray-800 break-words">{item.part_name}</p>
                          {item.notes && <p className="text-xs text-gray-500 italic break-words">{item.notes}</p>}
                          {/* Label shrinks and wraps; the money never breaks or spills out */}
                          <dl className="mt-2 grid grid-cols-[minmax(0,1fr)_auto] gap-x-3 gap-y-1 text-sm">
                              <dt className="text-gray-500">Quantity</dt>
                              <dd className="text-right font-medium">{item.quantity} {item.unit}</dd>
                              <dt className="text-gray-500">Your target price</dt>
                              <dd className="text-right whitespace-nowrap">{item.target_price ? money(item.target_price) : '-'}</dd>
                              <dt className="text-blue-800">Our price (each)</dt>
                              <dd className="text-right font-medium text-blue-900 whitespace-nowrap">{item.quoted_unit_price ? money(item.quoted_unit_price) : '-'}</dd>
                              <dt className="text-blue-800 font-bold">Line total</dt>
                              <dd className="text-right font-bold text-blue-900 whitespace-nowrap">{item.line_total ? money(item.line_total) : '-'}</dd>
                          </dl>
                      </div>
                  ))}
                  {hasQuote && (
                      <div className="bg-blue-100 rounded-lg p-3 flex flex-wrap items-center justify-between gap-x-2 gap-y-1">
                          <span className="font-bold text-blue-900">Total Quote</span>
                          <span className="font-bold text-blue-900 text-lg whitespace-nowrap">{money(req.total_quote_amount)}</span>
                      </div>
                  )}
              </div>

              {/* --- ITEMS: table on wider screens --- */}
              <div className="hidden sm:block bg-white rounded-lg shadow-sm overflow-hidden border border-gray-200 mb-6">
                  <div className="overflow-x-auto">
                    <table className="w-full text-left">
                        <thead className="bg-gray-50 text-xs text-gray-500 uppercase">
                            <tr>
                                <th className="p-3">Item</th>
                                <th className="p-3 text-center">Qty</th>
                                <th className="p-3 text-right">Target Price</th>
                                <th className="p-3 text-right bg-blue-50 text-blue-800">Quoted Unit</th>
                                <th className="p-3 text-right bg-blue-50 text-blue-800">Line Total</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {req.items.map((item, idx) => (
                                <tr key={idx}>
                                    <td className="p-3">
                                        <div className="font-bold text-gray-800">{item.part_name}</div>
                                        {item.notes && <div className="text-xs text-gray-500 italic">{item.notes}</div>}
                                    </td>
                                    <td className="p-3 text-center">
                                        {item.quantity} <span className="text-xs text-gray-500 block">{item.unit}</span>
                                    </td>
                                    <td className="p-3 text-right text-gray-500 whitespace-nowrap">
                                        {item.target_price ? money(item.target_price) : '-'}
                                    </td>
                                    <td className="p-3 text-right bg-blue-50 font-medium whitespace-nowrap">
                                        {item.quoted_unit_price ? money(item.quoted_unit_price) : <span className="text-gray-400">-</span>}
                                    </td>
                                    <td className="p-3 text-right bg-blue-50 font-bold text-gray-800 whitespace-nowrap">
                                        {item.line_total ? money(item.line_total) : <span className="text-gray-400">-</span>}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                        {hasQuote && (
                            <tfoot className="bg-blue-100">
                                <tr>
                                    <td colSpan="4" className="p-3 text-right font-bold text-blue-900">Total Quote:</td>
                                    <td className="p-3 text-right font-bold text-blue-900 text-lg whitespace-nowrap">
                                        {money(req.total_quote_amount)}
                                    </td>
                                </tr>
                            </tfoot>
                        )}
                    </table>
                  </div>
              </div>

              {req.admin_notes && (
                  <div className="bg-yellow-50 p-4 rounded border border-yellow-200 mb-6">
                      <h4 className="font-bold text-yellow-800 text-sm mb-1">Note from our team:</h4>
                      <p className="text-yellow-700 text-sm break-words">{req.admin_notes}</p>
                  </div>
              )}

              {req.status === 'accepted' && (
                  <div className="bg-green-50 p-4 rounded border border-green-200 text-center">
                      <FaCheckCircle className="text-green-500 text-3xl mx-auto mb-2" />
                      <h3 className="font-bold text-green-800 text-lg">Quote Accepted</h3>
                      <p className="text-green-700 text-sm">You accepted this quote. Our team will contact you with the invoice and delivery details.</p>
                  </div>
              )}

              {req.status === 'rejected' && (
                  <div className="bg-red-50 p-4 rounded border border-red-200 text-center">
                      <h3 className="font-bold text-red-700">Quote Declined</h3>
                      <p className="text-red-600 text-sm">You declined this quote. Use <strong>New Request</strong> on the list page if you would like another one.</p>
                  </div>
              )}
          </div>
      );
  }

  return null;
}

export default BulkRequestManager;